import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
    Store, Users, ShieldCheck, Heart, Radio, ShoppingBag, 
    Video, Star, MapPin, Globe, Search, Filter, ArrowRight, ArrowLeft,
    ChevronLeft, ChevronRight 
} from 'lucide-react';
import { getProducts, liveAPI, getActiveCountries, getVerifiedSellers } from '../api/client';
import { isRTL, getTranslation as t } from '../utils/translations';
import Spinner from '../components/Spinner';

export default function StoresPage() {
    const rtl = isRTL();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMarket, setSelectedMarket] = useState('ALL');
    const [activeTab, setActiveTab] = useState('ALL'); // ALL, FEATURED, POPULAR, FOLLOWED, LIVE
    const [followedStores, setFollowedStores] = useState([]);
    const [products, setProducts] = useState([]);
    const [activeLiveRooms, setActiveLiveRooms] = useState([]);
    const [dbSellers, setDbSellers] = useState([]);

    // Load Followed Stores from localStorage
    useEffect(() => {
        try {
            const raw = localStorage.getItem('luxvono_followed_sellers');
            if (raw) {
                setFollowedStores(JSON.parse(raw));
            }
        } catch (_) {}
    }, []);

    // Toggle Follow
    const toggleFollow = (storeId, e) => {
        e?.preventDefault();
        e?.stopPropagation();
        setFollowedStores(prev => {
            let updated;
            if (prev.includes(storeId)) {
                updated = prev.filter(id => id !== storeId);
            } else {
                updated = [...prev, storeId];
            }
            try {
                localStorage.setItem('luxvono_followed_sellers', JSON.stringify(updated));
            } catch (_) {}
            return updated;
        });
    };

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const [prodsRes, liveRes, sellersRes] = await Promise.all([
                    getProducts(0, 50).catch(() => ({ data: [] })),
                    liveAPI.getActiveRooms().catch(() => []),
                    getVerifiedSellers({ page: 0, size: 50 }).catch(() => null)
                ]);

                const pList = prodsRes?.data?.content || (Array.isArray(prodsRes?.data) ? prodsRes.data : []);
                setProducts(pList);
                if (Array.isArray(liveRes)) {
                    setActiveLiveRooms(liveRes);
                }
                const sList = sellersRes?.content || (Array.isArray(sellersRes) ? sellersRes : (sellersRes?.data?.content || []));
                if (Array.isArray(sList) && sList.length > 0) {
                    setDbSellers(sList);
                }
            } catch (err) {
                console.error('Failed to load stores page data:', err);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);

    // Curated & Live Database Verified Sellers
    const allStores = useMemo(() => {
        const mappedDbStores = dbSellers.map((s, idx) => ({
            id: `db-seller-${s.id || s.sellerId}`,
            slug: s.sellerSlug || `seller-${s.userId || s.id}`,
            sellerId: s.sellerId,
            userId: s.userId,
            name: rtl ? (s.businessNameAr || s.businessName || s.sellerUsername) : (s.businessName || s.sellerUsername),
            sellerName: s.sellerUsername || s.businessName,
            badge: s.verificationStatus === 'VERIFIED' ? 'VERIFIED_PARTNER' : (s.verificationStatus || 'VERIFIED_PARTNER'),
            country: s.countryCode || 'SA',
            countryName: s.countryCode === 'SA' ? (rtl ? 'المملكة العربية السعودية' : 'Saudi Arabia') : (s.countryCode || 'GCC'),
            bio: s.bio || (rtl ? 'متجر فاخر معتمد وموثق في منصة لوكسفونو.' : 'Verified luxury boutique on LUXVONO platform.'),
            avatar: s.storeLogoUrl || s.avatarUrl || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&auto=format&fit=crop&q=80',
            cover: s.storeCoverUrl || 'https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=600&auto=format&fit=crop&q=80',
            followersCount: s.followersCount || (2400 + idx * 350),
            rating: s.rating || 5.0,
            isLive: false,
            featuredProduct: products[idx % (products.length || 1)] || null
        }));

        const baseStores = [
            {
                id: 'store-1',
                slug: 'royal-haute-horlogerie',
                name: rtl ? 'دار الساعات الملكية الفاخرة' : 'Royal Haute Horlogerie',
                sellerName: rtl ? 'الشيخ أحمد المنصور' : 'Ahmed Al-Mansoor',
                badge: 'VERIFIED_PARTNER',
                country: 'SA',
                countryName: rtl ? 'المملكة العربية السعودية' : 'Saudi Arabia',
                bio: rtl ? 'وكيل معتمد ومستورد مباشر لأندر الساعات السويسرية والقطع الملكية المرصعة.' : 'Authorized purveyor of rare Swiss chronometers and bespoke complications.',
                avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&auto=format&fit=crop&q=80',
                cover: 'https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=600&auto=format&fit=crop&q=80',
                followersCount: 4820,
                rating: 4.9,
                isLive: true,
                liveTitle: rtl ? 'عرض حي: وصول ساعات رويال أوك إصدار خاص 🔴' : 'LIVE: Royal Oak Limited Drop 🔴',
                featuredProduct: products[0] || { name: 'Royal Chronometer', price: '45,000 SAR' }
            },
            {
                id: 'store-2',
                slug: 'florence-bespoke-leather',
                name: rtl ? 'فلورنسا للمصنوعات الجلدية' : 'Florence Bespoke Leather',
                sellerName: rtl ? 'ماركو وفاليريا روسي' : 'Valeria Rossi',
                badge: 'MAISON_VERIFIED',
                country: 'AE',
                countryName: rtl ? 'دولة الإمارات العربية المتحدة' : 'United Arab Emirates',
                bio: rtl ? 'حقائب وأحزمة جلد طبيعي مشغولة يدوياً في مشغلنا في توسكانا بإتقان ملكي.' : 'Handcrafted full-grain leather bags & trunks directly from our Tuscan atelier.',
                avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80',
                cover: 'https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=600&auto=format&fit=crop&q=80',
                followersCount: 3190,
                rating: 4.85,
                isLive: false,
                featuredProduct: products[1] || { name: 'Tuscan Leather Trunk', price: '18,500 SAR' }
            },
            {
                id: 'store-3',
                slug: 'somali-frankincense-amber',
                name: rtl ? 'كنوز اللبان والصمغ العربي الصومالي' : 'Somali Frankincense & Gum Heritage',
                sellerName: rtl ? 'عمر فارح حسن' : 'Omar Farah Hassan',
                badge: 'ORIGIN_CERTIFIED',
                country: 'SO',
                countryName: rtl ? 'الصومال وأرض الصومال' : 'Somalia & Somaliland',
                bio: rtl ? 'أجود أنواع لبان البوسويليا والمستكة والصمغ العربي النقي قطاف يدوي طازج.' : 'Wild-harvested Boswellia carterii resins, Royal Hojari & rare organic gum.',
                avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&auto=format&fit=crop&q=80',
                cover: 'https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?w=600&auto=format&fit=crop&q=80',
                followersCount: 1940,
                rating: 5.0,
                isLive: false,
                featuredProduct: products[2] || { name: 'Royal Hojari Frankincense', price: '1,200 SAR' }
            },
            {
                id: 'store-4',
                slug: 'yemen-sidr-honey-estate',
                name: rtl ? 'مناحل السدر الدوعني الملكي' : 'Royal Doani Sidr Honey Estates',
                sellerName: rtl ? 'سالم باحميد' : 'Salem Bahumaid',
                badge: 'ORGANIC_ROYAL',
                country: 'YE',
                countryName: rtl ? 'اليمن (وادي دوعن)' : 'Yemen (Wadi Doan)',
                bio: rtl ? 'عسل سدر دوعني جبلي درجة أولى قطاف شتوي مع فحص مخبري معتمد.' : 'Raw Mountain Sidr honey from ancient Yemeni hives, laboratory certified.',
                avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&auto=format&fit=crop&q=80',
                cover: 'https://images.unsplash.com/photo-1587049352846-4a222e784d38?w=600&auto=format&fit=crop&q=80',
                followersCount: 2750,
                rating: 4.95,
                isLive: false,
                featuredProduct: products[3] || { name: 'Winter Royal Sidr', price: '2,400 SAR' }
            }
        ];

        // Combine database verified sellers and curated flagship boutiques
        const combined = [...mappedDbStores];
        for (const b of baseStores) {
            if (!combined.some(c => c.slug === b.slug || c.name === b.name)) {
                combined.push(b);
            }
        }
        return combined;
    }, [products, dbSellers, rtl]);

    const filteredStores = allStores.filter(store => {
        if (activeTab === 'FOLLOWED' && !followedStores.includes(store.id)) return false;
        if (activeTab === 'LIVE' && !store.isLive) return false;
        if (selectedMarket !== 'ALL' && store.country !== selectedMarket) return false;
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            return store.name.toLowerCase().includes(term) ||
                   store.sellerName.toLowerCase().includes(term) ||
                   store.bio.toLowerCase().includes(term);
        }
        return true;
    });

    return (
        <div style={{ minHeight: '100vh', background: 'var(--lux-bg, #0B0F19)', color: 'var(--text, #F8FAFC)', padding: '40px 0 80px 0' }}>
            <div className="container" style={{ maxWidth: '1240px', margin: '0 auto', padding: '0 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                    <button 
                        type="button" 
                        onClick={() => {
                            if (window.history.state && window.history.state.idx > 0) {
                                navigate(-1);
                            } else {
                                navigate('/');
                            }
                        }} 
                        style={{ 
                            background: 'rgba(255,255,255,0.06)', 
                            border: '1px solid rgba(255,255,255,0.1)', 
                            borderRadius: '20px', 
                            cursor: 'pointer', 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            gap: '6px', 
                            padding: '6px 14px', 
                            color: '#94a3b8', 
                            fontSize: '13px', 
                            fontWeight: '600',
                            transition: 'all 0.2s ease'
                        }}
                        aria-label={rtl ? 'رجوع' : 'Back'}
                    >
                        {rtl ? <ChevronRight size={16} /> : <ChevronLeft size={16} />} 
                        <span>{rtl ? 'رجوع' : 'Back'}</span>
                    </button>
                </div>
                
                {/* Header Banner */}
                <div style={{ textAlign: 'center', marginBottom: '36px' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(212,175,55,0.12)', border: '1px solid rgba(212,175,55,0.3)', color: '#d4af37', padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '800', letterSpacing: '1px', marginBottom: '12px' }}>
                        <Store size={14} />
                        {rtl ? 'دليل المتاجر والشركاء المعتمدين' : 'VERIFIED STORES & MAISONS'}
                    </span>
                    <h1 style={{ fontSize: '32px', fontWeight: '900', margin: '0 0 10px 0', background: 'linear-gradient(135deg, #ffffff 0%, #cbd5e1 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                        {rtl ? 'استكشف المتاجر الفاخرة وتواصل مع أصحابها' : 'Discover Luxury Maisons & Independent Creators'}
                    </h1>
                    <p style={{ fontSize: '14px', color: '#94a3b8', maxWidth: '640px', margin: '0 auto' }}>
                        {rtl 
                            ? 'تسوق مباشرة من كبار الموردين والتجار الموثقين في المملكة والخليج والشرق الأوسط والعالم.' 
                            : 'Connect directly with certified sovereign merchants, watchmakers, artisans, and live hosts.'}
                    </p>
                </div>

                {/* Filter & Search Ribbon */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', background: '#1e293b', border: '1px solid #334155', borderRadius: '16px', padding: '14px 20px', marginBottom: '32px' }}>
                    {/* Tabs */}
                    <div style={{ display: 'flex', gap: '8px', overflowX: 'auto' }}>
                        {[
                            { key: 'ALL', label: rtl ? 'كافة المتاجر' : 'All Stores' },
                            { key: 'LIVE', label: rtl ? '🔴 بث حي مباشر' : '🔴 Live Now' },
                            { key: 'FOLLOWED', label: rtl ? `المتاجر المتابعة (${followedStores.length})` : `Following (${followedStores.length})` }
                        ].map(t => (
                            <button
                                key={t.key}
                                onClick={() => setActiveTab(t.key)}
                                style={{
                                    background: activeTab === t.key ? '#2563eb' : 'transparent',
                                    color: activeTab === t.key ? '#ffffff' : '#94a3b8',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    fontWeight: '700',
                                    cursor: 'pointer'
                                }}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {/* Market Filter & Search */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <select
                            value={selectedMarket}
                            onChange={e => setSelectedMarket(e.target.value)}
                            style={{ background: '#0b0f19', border: '1px solid #334155', color: '#cbd5e1', padding: '9px 14px', borderRadius: '8px', fontSize: '13px' }}
                        >
                            <option value="ALL">{rtl ? 'كافة الدول والأسواق' : 'All Markets'}</option>
                            <option value="SA">🇸🇦 {rtl ? 'السعودية' : 'Saudi Arabia'}</option>
                            <option value="AE">🇦🇪 {rtl ? 'الإمارات' : 'UAE'}</option>
                            <option value="SO">🇸🇴 {rtl ? 'الصومال وأرض الصومال' : 'Somalia'}</option>
                            <option value="YE">🇾🇪 {rtl ? 'اليمن' : 'Yemen'}</option>
                            <option value="OM">🇴🇲 {rtl ? 'عُمان' : 'Oman'}</option>
                        </select>

                        <div style={{ position: 'relative', width: '220px' }}>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                placeholder={rtl ? 'بحث باسم المتجر...' : 'Search stores...'}
                                style={{ width: '100%', padding: '9px 12px 9px 32px', background: '#0b0f19', border: '1px solid #334155', borderRadius: '8px', color: '#fff', fontSize: '12px', boxSizing: 'border-box' }}
                            />
                            <Search size={14} color="#64748b" style={{ position: 'absolute', left: '10px', top: '12px' }} />
                        </div>
                    </div>
                </div>

                {/* Stores Grid */}
                {loading ? (
                    <div style={{ padding: '80px 0', textAlign: 'center' }}>
                        <Spinner />
                    </div>
                ) : filteredStores.length === 0 ? (
                    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '16px', padding: '60px 20px', textAlign: 'center', color: '#94a3b8' }}>
                        <Store size={48} style={{ opacity: 0.3, marginBottom: '14px' }} />
                        <h3 style={{ margin: '0 0 6px 0', fontSize: '16px', color: '#f8fafc' }}>
                            {rtl ? 'لا توجد متاجر مطابقة' : 'No stores matching this criteria'}
                        </h3>
                        <p style={{ margin: 0, fontSize: '13px' }}>
                            {rtl ? 'جرب البحث بكلمات أخرى أو اختر دولة أخرى.' : 'Try changing search terms or market filters.'}
                        </p>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '24px' }}>
                        {filteredStores.map(store => {
                            const isFollowed = followedStores.includes(store.id);

                            return (
                                <div
                                    key={store.id}
                                    style={{
                                        background: '#1e293b',
                                        border: `1px solid ${store.isLive ? 'rgba(239,68,68,0.5)' : '#334155'}`,
                                        borderRadius: '16px',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        boxShadow: store.isLive ? '0 8px 24px rgba(239,68,68,0.15)' : 'none',
                                        transition: 'transform 0.2s ease, border-color 0.2s ease'
                                    }}
                                >
                                    {/* Cover Header */}
                                    <div style={{ height: '120px', background: `url(${store.cover}) center/cover`, position: 'relative' }}>
                                        {store.isLive && (
                                            <span style={{ position: 'absolute', top: '10px', left: '10px', background: 'rgba(239,68,68,0.95)', color: '#fff', fontSize: '11px', fontWeight: '800', padding: '3px 8px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '4px', backdropFilter: 'blur(4px)' }}>
                                                <Radio size={12} />
                                                LIVE NOW
                                            </span>
                                        )}

                                        <button
                                            type="button"
                                            onClick={(e) => toggleFollow(store.id, e)}
                                            style={{
                                                position: 'absolute',
                                                top: '10px',
                                                right: '10px',
                                                background: isFollowed ? 'rgba(239,68,68,0.85)' : 'rgba(0,0,0,0.6)',
                                                border: 'none',
                                                color: '#fff',
                                                padding: '6px 12px',
                                                borderRadius: '20px',
                                                fontSize: '11px',
                                                fontWeight: '700',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '5px',
                                                backdropFilter: 'blur(4px)'
                                            }}
                                        >
                                            <Heart size={12} fill={isFollowed ? '#fff' : 'none'} />
                                            <span>{isFollowed ? (rtl ? 'مُتابَع' : 'Following') : (rtl ? '+ متابعة' : '+ Follow')}</span>
                                        </button>
                                    </div>

                                    {/* Content Body */}
                                    <div style={{ padding: '0 20px 20px 20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                        {/* Avatar & Title Row */}
                                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '14px', marginTop: '-30px', marginBottom: '12px' }}>
                                            <div style={{ width: '60px', height: '60px', borderRadius: '12px', overflow: 'hidden', border: '3px solid #1e293b', background: '#0b0f19', flexShrink: 0 }}>
                                                <img src={store.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            </div>
                                            <div style={{ minWidth: 0 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '800', color: '#f8fafc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {store.name}
                                                    </h3>
                                                    <ShieldCheck size={16} color="#d4af37" style={{ flexShrink: 0 }} />
                                                </div>
                                                <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                                                    {store.sellerName} • {store.countryName}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Bio */}
                                        <p style={{ fontSize: '12px', color: '#cbd5e1', lineHeight: '1.5', margin: '0 0 16px 0', flex: 1 }}>
                                            {store.bio}
                                        </p>

                                        {/* Stats Row */}
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#0b0f19', borderRadius: '10px', marginBottom: '16px', fontSize: '11px' }}>
                                            <div>
                                                <span style={{ color: '#94a3b8', display: 'block' }}>{rtl ? 'المتابعون' : 'Followers'}</span>
                                                <strong style={{ color: '#f8fafc', fontSize: '13px' }}>{store.followersCount.toLocaleString()}</strong>
                                            </div>
                                            <div style={{ borderLeft: '1px solid #334155', paddingLeft: '14px' }}>
                                                <span style={{ color: '#94a3b8', display: 'block' }}>{rtl ? 'تقييم المتجر' : 'Rating'}</span>
                                                <strong style={{ color: '#d4af37', fontSize: '13px' }}>★ {store.rating}</strong>
                                            </div>
                                            <div style={{ borderLeft: '1px solid #334155', paddingLeft: '14px' }}>
                                                <span style={{ color: '#94a3b8', display: 'block' }}>{rtl ? 'التوثيق' : 'Status'}</span>
                                                <strong style={{ color: '#10b981', fontSize: '11px' }}>VERIFIED</strong>
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <Link
                                                to={`/seller/${store.slug}`}
                                                style={{
                                                    flex: 1,
                                                    textAlign: 'center',
                                                    background: 'linear-gradient(135deg, #d4af37 0%, #aa8214 100%)',
                                                    color: '#0b0f19',
                                                    padding: '9px 14px',
                                                    borderRadius: '8px',
                                                    fontWeight: '700',
                                                    fontSize: '12px',
                                                    textDecoration: 'none'
                                                }}
                                            >
                                                {rtl ? 'زيارة المتجر' : 'Visit Store'}
                                            </Link>
                                            
                                            {store.isLive && (
                                                <Link
                                                    to="/live"
                                                    style={{
                                                        background: '#ef4444',
                                                        color: '#fff',
                                                        padding: '9px 12px',
                                                        borderRadius: '8px',
                                                        fontWeight: '700',
                                                        fontSize: '12px',
                                                        textDecoration: 'none',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}
                                                >
                                                    <Radio size={13} />
                                                    <span>{rtl ? 'مشاهدة البث' : 'Watch Live'}</span>
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
